import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export const PresentationContext = createContext(false);
export const usePresentationMode = () => useContext(PresentationContext);

export const usePresentationView = () => {
  const containerRef = useRef(null);
  const hadFullscreen = useRef(false);
  const savedScroll = useRef(0);
  const active = useRef(false);
  const [presenting, setPresenting] = useState(false);
  const [fullscreenUnavailable, setFullscreenUnavailable] = useState(false);

  const finishExit = useCallback(() => {
    active.current = false;
    hadFullscreen.current = false;
    setPresenting(false);
    setFullscreenUnavailable(false);
    requestAnimationFrame(() => {
      document.querySelector('[data-testid="showcase-enter-presentation-mode-button"]')?.focus({ preventScroll: true });
      window.scrollTo({ top: savedScroll.current, behavior: "instant" });
    });
  }, []);

  const exit = useCallback(async () => {
    if (document.fullscreenElement === containerRef.current && document.exitFullscreen) {
      try { await document.exitFullscreen(); } catch (_) { /* Keep the in-app exit available. */ }
    }
    finishExit();
  }, [finishExit]);

  const enter = useCallback(async () => {
    savedScroll.current = window.scrollY;
    active.current = true;
    setPresenting(true);
    setFullscreenUnavailable(false);
    const container = containerRef.current;
    // Call synchronously from the button gesture; never auto-request fullscreen.
    if (!container?.requestFullscreen) {
      setFullscreenUnavailable(true);
      return;
    }
    try {
      await container.requestFullscreen();
      if (!active.current) {
        if (document.fullscreenElement === container) await document.exitFullscreen();
        return;
      }
      hadFullscreen.current = document.fullscreenElement === container;
      setFullscreenUnavailable(!hadFullscreen.current);
    } catch (_) {
      if (active.current) setFullscreenUnavailable(true);
    }
  }, []);

  useEffect(() => {
    const changed = () => {
      if (document.fullscreenElement === containerRef.current) hadFullscreen.current = true;
      else if (hadFullscreen.current) finishExit();
    };
    document.addEventListener("fullscreenchange", changed);
    return () => document.removeEventListener("fullscreenchange", changed);
  }, [finishExit]);

  useEffect(() => {
    if (!presenting) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    containerRef.current?.scrollTo({ top: 0, behavior: "instant" });
    const escape = (event) => { if (event.key === "Escape") exit(); };
    document.addEventListener("keydown", escape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", escape);
    };
  }, [presenting, exit]);

  return { containerRef, presenting, fullscreenUnavailable, enter, exit };
};
